/**
 * Μικρό dropdown πολλαπλής επιλογής (checkboxes) για τα φίλτρα της σελίδας Πανελλήνιες.
 * Το native <select multiple> θέλει Ctrl/Cmd+κλικ και δεν δουλεύει καλά σε κινητό.
 *
 * Λογική φίλτρων: μέσα στο ίδιο dropdown οι επιλογές είναι «ή» (OR)· ανάμεσα σε
 * διαφορετικά dropdown «και» (AND) — αυτό το χειρίζεται ο καλών.
 */
export interface MsOption {
  value: string;
  label: string;
  /** Επικεφαλίδα ομάδας (π.χ. κεφάλαιο βιβλίου)· διαδοχικές επιλογές με ίδια ομάδα μπαίνουν κάτω από μία επικεφαλίδα. */
  group?: string;
}

const openMenus = new Set<MultiSelect>();

function closeAll(except?: MultiSelect): void {
  openMenus.forEach((m) => {
    if (m !== except) m.close();
  });
}

document.addEventListener('click', (e) => {
  openMenus.forEach((m) => {
    if (!m.host.contains(e.target as Node)) m.close();
  });
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeAll();
});

export class MultiSelect {
  private readonly button: HTMLButtonElement;
  private readonly panel: HTMLElement;
  private readonly list: HTMLElement;
  private search: HTMLInputElement | null = null;
  private options: MsOption[] = [];
  private readonly selected = new Set<string>();

  constructor(
    readonly host: HTMLElement,
    private readonly placeholder: string,
    private readonly onChange: () => void,
    /** Κείμενο όταν είναι επιλεγμένες ΟΛΕΣ οι επιλογές (και τότε το φίλτρο δεν φαίνεται ενεργό). */
    private readonly allText?: string
  ) {
    host.classList.add('pan-ms');
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'pan-ms-btn';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));

    this.panel = document.createElement('div');
    this.panel.className = 'pan-ms-panel';
    this.panel.hidden = true;
    this.list = document.createElement('div');
    this.list.className = 'pan-ms-list';
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-multiselectable', 'true');

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'pan-ms-clear';
    clear.textContent = 'Καθαρισμός επιλογής';
    clear.addEventListener('click', () => {
      this.clear();
      this.onChange();
    });

    this.panel.append(this.list, clear);
    host.append(this.button, this.panel);
    this.renderSummary();
  }

  get values(): string[] {
    return [...this.selected];
  }

  /** Οι ετικέτες των επιλεγμένων τιμών, με τη σειρά των επιλογών. */
  get labels(): string[] {
    return this.options.filter((o) => this.selected.has(o.value)).map((o) => o.label);
  }

  setOptions(options: MsOption[], searchable: boolean): void {
    this.options = options;
    if (searchable && this.search === null) {
      this.search = document.createElement('input');
      this.search.type = 'search';
      this.search.autocomplete = 'off';
      this.search.name = 'opt-filter';
      this.search.className = 'pan-ms-search';
      this.search.placeholder = 'Αναζήτηση στη λίστα…';
      this.search.addEventListener('input', () => this.renderList());
      this.panel.insertBefore(this.search, this.list);
    }
    const scroll = this.list.scrollTop; // οι επιλογές ξαναχτίζονται σε κάθε αλλαγή φίλτρων
    this.renderList();
    this.list.scrollTop = scroll;
    this.renderSummary();
  }

  /** Ορίζει την επιλογή (π.χ. προεπιλογή) χωρίς να καλέσει το onChange. */
  select(values: string[]): void {
    this.selected.clear();
    values.forEach((v) => this.selected.add(v));
    this.renderList();
    this.renderSummary();
  }

  clear(): void {
    this.selected.clear();
    this.renderList();
    this.renderSummary();
  }

  open(): void {
    closeAll(this);
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    openMenus.add(this);
    this.search?.focus();
  }

  close(): void {
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    openMenus.delete(this);
  }

  private renderSummary(): void {
    const n = this.selected.size;
    const all = this.allText !== undefined && this.options.length > 0 && n === this.options.length;
    let text = this.placeholder;
    if (all) {
      text = this.allText ?? text;
    } else if (n === 1) {
      const only = this.options.find((o) => this.selected.has(o.value));
      text = only !== undefined ? only.label : '1 επιλογή';
    } else if (n > 1) {
      text = n + ' επιλογές';
    }
    this.button.replaceChildren(
      Object.assign(document.createElement('span'), { className: 'pan-ms-text', textContent: text }),
      Object.assign(document.createElement('span'), { className: 'pan-ms-caret', textContent: '▾' })
    );
    this.button.classList.toggle('pan-ms-active', n > 0 && !all);
  }

  private renderList(): void {
    const q = this.search === null ? '' : norm(this.search.value.trim());
    this.list.replaceChildren();
    let lastGroup: string | undefined;
    for (const o of this.options) {
      if (q !== '' && !norm(o.label + ' ' + (o.group ?? '')).includes(q)) continue;
      if (o.group !== undefined && o.group !== lastGroup) {
        this.list.appendChild(Object.assign(document.createElement('div'), { className: 'pan-ms-group', textContent: o.group }));
      }
      lastGroup = o.group;
      const row = document.createElement('label');
      row.className = 'pan-ms-opt';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = this.selected.has(o.value);
      cb.addEventListener('change', () => {
        if (cb.checked) this.selected.add(o.value);
        else this.selected.delete(o.value);
        this.renderSummary();
        this.onChange();
      });
      row.append(cb, Object.assign(document.createElement('span'), { textContent: o.label }));
      this.list.appendChild(row);
    }
    if (this.list.childElementCount === 0) {
      this.list.appendChild(Object.assign(document.createElement('div'), { className: 'pan-ms-empty', textContent: 'Καμία επιλογή.' }));
    }
  }
}

function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ς/g, 'σ');
}
